import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { StandardDashboardContent, StandardDashboardCharts } from "../DashboardContent";
import { Stats } from "./Stats";
import { VotesPerDay } from "../charts/VotesPerDay";
import { DEFAULT_VOTES_TIME_SELECTION, getVotesBucketCount, } from "../charts/votesTimeRange";
import { daysBefore, formatDate, getToday } from "../charts/Charts";
import { VotersByChannel } from "../charts/VotersByChannel";
import { toVotersByChannelRows } from "../charts/votersByChannelData";
import { useRecordContext } from "react-admin";
import { SettingsContext } from "@/providers/SettingsContextProvider";
import { useQuery } from "@apollo/client";
import { GET_ELECTION_STATS } from "@/queries/GetElectionStats";
import { translateFromPresentation } from "@sequentech/ui-core";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { useTranslation } from "react-i18next";
export default function DashboardElection() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    var i18n = useTranslation().i18n;
    var tenantId = useTenantStore()[0];
    var globalSettings = useContext(SettingsContext).globalSettings;
    var record = useRecordContext();
    var _s = useState(DEFAULT_VOTES_TIME_SELECTION), votesTimeSelection = _s[0], setVotesTimeSelection = _s[1];
    var electionAlias = record
        ? translateFromPresentation(record.presentation, "alias", i18n.language)
        : undefined;
    var endDate = getToday();
    var startDate = daysBefore(endDate, 6);
    var userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Ensure required parameters are set before running the query.
    var canQueryStats = Boolean(tenantId && (record === null || record === void 0 ? void 0 : record.election_event_id) && (record === null || record === void 0 ? void 0 : record.id));
    var _t = useQuery(GET_ELECTION_STATS, {
        variables: {
            tenantId: tenantId,
            electionEventId: record === null || record === void 0 ? void 0 : record.election_event_id,
            electionId: record === null || record === void 0 ? void 0 : record.id,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            electionAlias: electionAlias !== null && electionAlias !== void 0 ? electionAlias : undefined,
            userTimezone: userTimezone,
            timeResolution: votesTimeSelection.resolution,
            bucketCount: getVotesBucketCount(votesTimeSelection),
        },
        pollInterval: globalSettings.QUERY_POLL_INTERVAL_MS,
        skip: !canQueryStats,
    }), loading = _t.loading, error = _t.error, dataStats = _t.data;
    if (loading) {
        return _jsx(CircularProgress, {});
    }
    var stats = (_b = (_a = dataStats === null || dataStats === void 0 ? void 0 : dataStats.election) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.statistics;
    var metrics = {
        eligibleVotersCount: (_d = (_c = dataStats === null || dataStats === void 0 ? void 0 : dataStats.users) === null || _c === void 0 ? void 0 : _c.count) !== null && _d !== void 0 ? _d : "-",
        votersCount: (_f = (_e = dataStats === null || dataStats === void 0 ? void 0 : dataStats.stats) === null || _e === void 0 ? void 0 : _e.total_distinct_voters) !== null && _f !== void 0 ? _f : "-",
        areasCount: (_h = (_g = dataStats === null || dataStats === void 0 ? void 0 : dataStats.stats) === null || _g === void 0 ? void 0 : _g.total_areas) !== null && _h !== void 0 ? _h : "-",
        emailsSentCount: (_j = stats === null || stats === void 0 ? void 0 : stats.num_emails_sent) !== null && _j !== void 0 ? _j : "-",
        smsSentCount: (_k = stats === null || stats === void 0 ? void 0 : stats.num_sms_sent) !== null && _k !== void 0 ? _k : "-",
        messagesSentCount: {
            WHATSAPP: (_l = stats === null || stats === void 0 ? void 0 : stats.num_whatsapp_sent) !== null && _l !== void 0 ? _l : "-",
            VIBER: (_m = stats === null || stats === void 0 ? void 0 : stats.num_viber_sent) !== null && _m !== void 0 ? _m : "-",
            MESSENGER: (_o = stats === null || stats === void 0 ? void 0 : stats.num_messenger_sent) !== null && _o !== void 0 ? _o : "-",
        },
    };
    var cardHeight = 250;
    return (_jsx(StandardDashboardContent, { className: "dashboard", children: _jsxs(Box, { children: [_jsx(Stats, { metrics: metrics }), _jsxs(StandardDashboardCharts, { children: [_jsx(VotesPerDay, { data: (_q = (_p = dataStats === null || dataStats === void 0 ? void 0 : dataStats.stats) === null || _p === void 0 ? void 0 : _p.votes_per_day) !== null && _q !== void 0 ? _q : null, unavailable: !!error, height: cardHeight, selection: votesTimeSelection, onSelectionChange: setVotesTimeSelection }), _jsx(VotersByChannel, { data: toVotersByChannelRows((_r = dataStats === null || dataStats === void 0 ? void 0 : dataStats.stats) === null || _r === void 0 ? void 0 : _r.voters_by_channel), height: cardHeight })] })] }) }));
}
